import { useState, useEffect } from 'react';
import { 
  ArrowLeft, ArrowRight, RefreshCw, Clock, CheckCircle, 
  AlertTriangle, DollarSign, FileText, Users, Play, Pause,
  Lock, Unlock, Eye, Settings2, Send, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

// All investment states in lifecycle order
const ALL_INV_STATES = ['INTEREST', 'EOI_PAID', 'DOC_SENT', 'DOC_SIGNED', 'CAPITAL_PAID', 'COMPLETED', 'CANCELLED', 'REFUNDED'];

// Override config per target state
const OVERRIDE_CONFIG = {
  INTEREST: { label: 'Interest Registered', needsAmount: false },
  EOI_PAID: { label: 'EOI Paid', needsAmount: true, defaultAmount: 25000 },
  DOC_SENT: { label: 'Document Sent', needsAmount: false },
  DOC_SIGNED: { label: 'Document Signed', needsAmount: false },
  CAPITAL_PAID: { label: 'Capital Paid', needsAmount: true },
  COMPLETED: { label: 'Completed', needsAmount: false },
  CANCELLED: { label: 'Cancelled', needsAmount: false },
  REFUNDED: { label: 'Refunded', needsAmount: true },
};

// Contextual actions map
const ACTION_MAP = {
  'resend_signup_link': { endpoint: 'resend-signup', label: 'Resend Signup Link', needsAmount: false },
  'record_eoi_payment': { endpoint: 'record-eoi', label: 'Record EOI Payment', needsAmount: true, defaultAmount: 25000 },
  'send_document': { endpoint: 'send-document', label: 'Send LLP Agreement', needsAmount: false },
  'resend_document': { endpoint: 'resend-document', label: 'Resend LLP Agreement', needsAmount: false },
  'record_capital_payment': { endpoint: 'record-capital', label: 'Record Capital Payment', needsAmount: true },
  'mark_completed': { endpoint: 'mark-completed', label: 'Mark Completed', needsAmount: false },
  'cancel': { endpoint: 'cancel', label: 'Cancel Investment', needsAmount: false },
  'refund': { endpoint: 'refund', label: 'Refund', needsAmount: true },
};

// Mock lifecycle data
const STATE_CONFIG = {
  DRAFT: { color: 'bg-gray-500', text: 'text-gray-400', icon: FileText },
  OPEN: { color: 'bg-[#4ADE80]', text: 'text-[#4ADE80]', icon: Play },
  PAUSED: { color: 'bg-yellow-500', text: 'text-yellow-400', icon: Pause },
  FUNDED: { color: 'bg-purple-500', text: 'text-purple-400', icon: CheckCircle },
  ESCROW_RELEASED: { color: 'bg-orange-500', text: 'text-orange-400', icon: DollarSign },
  RERA_PAYMENT_DONE: { color: 'bg-cyan-500', text: 'text-cyan-400', icon: CheckCircle },
  CLOSED: { color: 'bg-blue-500', text: 'text-blue-400', icon: Lock },
  EXITED: { color: 'bg-white', text: 'text-white', icon: Unlock },
};

const INV_STATE_CONFIG = {
  INTEREST: { color: 'text-gray-400', bg: 'bg-gray-500/20', dot: 'bg-gray-400' },
  EOI_PAID: { color: 'text-blue-400', bg: 'bg-blue-500/20', dot: 'bg-blue-400' },
  DOC_SENT: { color: 'text-amber-400', bg: 'bg-amber-500/20', dot: 'bg-amber-400' },
  DOC_SIGNED: { color: 'text-purple-400', bg: 'bg-purple-500/20', dot: 'bg-purple-400' },
  CAPITAL_PAID: { color: 'text-emerald-400', bg: 'bg-emerald-500/20', dot: 'bg-emerald-400' },
  COMPLETED: { color: 'text-gray-300', bg: 'bg-white/10', dot: 'bg-gray-300' },
  CANCELLED: { color: 'text-red-400', bg: 'bg-red-500/20', dot: 'bg-red-400' },
  REFUNDED: { color: 'text-orange-400', bg: 'bg-orange-500/20', dot: 'bg-orange-400' },
};

const formatINR = (val) => {
  if (!val) return '0';
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return `${(val / 1000).toFixed(0)} K`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleDateString('en-IN', { 
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const StateBadge = ({ state }) => {
  const colors = INV_STATE_CONFIG[state] || INV_STATE_CONFIG.INTEREST;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-mono ${colors.bg} ${colors.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
      {state.replace(/_/g, ' ')}
    </span>
  );
};

const OverrideDropdown = ({ inv, onOverride }) => {
  const [open, setOpen] = useState(false);
  const availableStates = ALL_INV_STATES.filter(s => s !== inv.state);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs px-2 py-1 border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 transition-colors whitespace-nowrap flex items-center gap-1"
        data-testid={`override-btn-${inv.investmentId}`}>
        <Settings2 className="w-3 h-3" /> Override
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-[#1a1a1a] border border-white/10 py-1 min-w-[180px] shadow-xl" data-testid={`override-menu-${inv.investmentId}`}>
            <p className="px-3 py-1.5 text-xs text-gray-600 uppercase tracking-wider border-b border-white/5">Move to state</p>
            {availableStates.map(state => {
              const colors = INV_STATE_CONFIG[state] || INV_STATE_CONFIG.INTEREST;
              return (
                <button key={state}
                  onClick={() => { setOpen(false); onOverride(inv, state); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2 transition-colors"
                  data-testid={`override-to-${state}-${inv.investmentId}`}>
                  <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                  <span className={colors.color}>{state.replace(/_/g, ' ')}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default function AdminUnitLifecycle({ unitId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [showEscrowRelease, setShowEscrowRelease] = useState(false);
  const [showReraPayment, setShowReraPayment] = useState(false);
  const [reraPaymentForm, setReraPaymentForm] = useState({ reference: '', amount: '', notes: '' });
  
  // Investor filter
  const [stateFilter, setStateFilter] = useState('ALL');
  
  // Override modal
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideAmount, setOverrideAmount] = useState('');
  const [overrideTxnId, setOverrideTxnId] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);

  // Action modal
  const [actionModal, setActionModal] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [actionAmount, setActionAmount] = useState('');
  const [actionTxnId, setActionTxnId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (unitId) fetchLifecycle();
  }, [unitId]);

  const fetchLifecycle = async () => {
    try {
      const response = await api.get(`/units/${unitId}/lifecycle`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load lifecycle data');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTransition = async (toState) => {
    if (!window.confirm(`Transition unit to ${toState}?`)) return;
    setTransitioning(true);
    try {
      await api.post(`/units/${unitId}/transition`, { toState, performedBy: 'admin', reason: `Manual transition to ${toState}` });
      fetchLifecycle();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transition failed');
    } finally {
      setTransitioning(false);
    }
  };

  const handleReleaseEscrow = async () => {
    if (!window.confirm('Release escrow funds to LLP? This action is irreversible.')) return;
    setTransitioning(true);
    try {
      await api.post(`/units/${unitId}/release-escrow`, null, { params: { confirmed_by: 'admin' } });
      setShowEscrowRelease(false);
      fetchLifecycle();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Release failed');
    } finally {
      setTransitioning(false);
    }
  };

  const handleReraPayment = async () => {
    if (!reraPaymentForm.reference || !reraPaymentForm.amount) { toast.error('Fill in payment reference and amount'); return; }
    if (!window.confirm('Confirm RERA payment to developer?')) return;
    setTransitioning(true);
    try {
      await api.post(`/units/${unitId}/confirm-rera-payment`, {
        paymentReference: reraPaymentForm.reference,
        paymentAmount: parseFloat(reraPaymentForm.amount),
        notes: reraPaymentForm.notes || undefined
      }, { params: { confirmed_by: 'admin' } });
      setShowReraPayment(false);
      setReraPaymentForm({ reference: '', amount: '', notes: '' });
      fetchLifecycle();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'RERA payment failed');
    } finally {
      setTransitioning(false);
    }
  };

  const handleCloseUnit = async () => {
    if (!window.confirm('Close this unit?')) return;
    setTransitioning(true);
    try {
      await api.post(`/units/${unitId}/close`, null, { params: { confirmed_by: 'admin' } });
      fetchLifecycle();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Close unit failed');
    } finally {
      setTransitioning(false);
    }
  };

  const handleOverride = (inv, targetState) => {
    const config = OVERRIDE_CONFIG[targetState];
    setOverrideModal({ inv, targetState, config });
    setOverrideReason('');
    setOverrideAmount(config.needsAmount ? (config.defaultAmount ? String(config.defaultAmount) : (inv.amount ? String(inv.amount) : '')) : '');
    setOverrideTxnId('');
  };

  const executeOverride = async () => {
    if (overrideReason.length < 10) { toast.error('Reason must be at least 10 characters'); return; }
    const { inv, targetState, config } = overrideModal;
    setOverrideLoading(true);
    try {
      const payload = { adminId: 'admin', reason: overrideReason, targetState };
      if (config.needsAmount && overrideAmount) {
        payload.amount = parseFloat(overrideAmount);
        if (overrideTxnId) payload.transactionId = overrideTxnId;
      }
      const res = await api.post(`/investments/${inv.investmentId}/override-state`, payload);
      if (res.data.success) {
        toast.success(`Override: ${inv.investorName} \u2192 ${targetState}`);
        setOverrideModal(null);
        fetchLifecycle();
      } else {
        toast.error(res.data.error || 'Override failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Override failed');
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleAction = (inv, action) => {
    const config = ACTION_MAP[action];
    if (!config) { toast.error(`Unknown action: ${action}`); return; }
    setActionModal({ inv, action, config });
    setActionReason('');
    setActionAmount(config.defaultAmount ? String(config.defaultAmount) : (inv.amount ? String(inv.amount) : ''));
    setActionTxnId('');
  };

  const executeAction = async () => {
    if (actionReason.length < 10) { toast.error('Reason must be at least 10 characters'); return; }
    const { inv, config } = actionModal;
    setActionLoading(true);
    try {
      const payload = { adminId: 'admin', reason: actionReason };
      if (config.needsAmount) {
        payload.amount = parseFloat(actionAmount);
        if (!payload.amount || payload.amount <= 0) { toast.error('Enter a valid amount'); setActionLoading(false); return; }
        if (actionTxnId) payload.transactionId = actionTxnId;
      }
      const res = await api.post(`/investments/${inv.investmentId}/actions/${config.endpoint}`, payload);
      if (res.data.success) {
        toast.success(`${config.label}: ${inv.investorName || inv.investor?.name} \u2192 ${res.data.state}`);
        setActionModal(null);
        fetchLifecycle();
      } else {
        toast.error(res.data.error || 'Action failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="p-10 flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 text-gray-500 animate-spin" /></div>;
  if (!data) return (
    <div className="p-10">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white mb-6 flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
      <p className="text-gray-500">Unit not found</p>
    </div>
  );

  const { unit, lifecycle, escrowSummary, investments, recentEvents, validTransitions, escrow, financial, availableTransitions } = data;
  const currentState = lifecycle?.currentState || 'DRAFT';
  const fundingPercent = (escrowSummary?.totalInEscrow || escrow?.totalCollected || 0) / (unit?.financial?.capitalTarget || financial?.capitalTarget || 1) * 100;

  // Investor filtering
  const allInvestments = investments || [];
  const stateCounts = allInvestments.reduce((acc, inv) => {
    const s = inv.state || 'INTEREST';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const filteredInvestments = stateFilter === 'ALL' ? allInvestments : allInvestments.filter(i => i.state === stateFilter);
  const overdueCount = allInvestments.filter(i => i.isOverdue).length;

  const modalInputCls = "w-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-white focus:border-white/30 focus:outline-none";

  return (
    <div className="p-10" data-testid="unit-lifecycle-view">
      {/* Override Modal */}
      {overrideModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="override-modal">
          <div className="bg-[#1a1a1a] border border-white/10 p-6 w-[480px] max-w-[90vw]">
            <div className="flex items-center gap-2 mb-1">
              <Settings2 className="w-4 h-4 text-amber-400" />
              <h3 className="text-white text-base">Manual Override</h3>
            </div>
            <p className="text-sm text-gray-500 mb-2">
              {overrideModal.inv.investorName || overrideModal.inv.investor?.name} ({overrideModal.inv.investmentId})
            </p>
            <div className="flex items-center gap-2 mb-4 p-3 bg-white/5 border border-white/10">
              <StateBadge state={overrideModal.inv.state} />
              <span className="text-gray-500 text-xs">→</span>
              <StateBadge state={overrideModal.targetState} />
            </div>
            {overrideModal.config.needsAmount && (
              <div className="mb-3">
                <label className="text-xs text-gray-500 uppercase mb-1 block">Amount (INR)</label>
                <input type="number" value={overrideAmount} onChange={e => setOverrideAmount(e.target.value)}
                  className={`${modalInputCls} font-mono`} data-testid="override-amount" />
              </div>
            )}
            {overrideModal.config.needsAmount && (
              <div className="mb-3">
                <label className="text-xs text-gray-500 uppercase mb-1 block">Transaction Ref (optional)</label>
                <input value={overrideTxnId} onChange={e => setOverrideTxnId(e.target.value)}
                  className={modalInputCls} placeholder="NEFT/UPI reference" data-testid="override-txn-id" />
              </div>
            )}
            <div className="mb-4">
              <label className="text-xs text-gray-500 uppercase mb-1 block">Reason (min 10 chars) *</label>
              <textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                className={`${modalInputCls} h-20 resize-none`} placeholder="Why is this manual override being applied?"
                data-testid="override-reason" />
              <p className={`text-xs mt-1 ${overrideReason.length >= 10 ? 'text-gray-600' : 'text-red-400'}`}>
                {overrideReason.length}/10 characters minimum
              </p>
            </div>
            <div className="p-3 mb-4 bg-amber-500/5 border border-amber-500/20">
              <p className="text-xs text-amber-400">This is a manual override and will be recorded in the audit trail. Use only for edge cases where the normal flow cannot proceed.</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setOverrideModal(null)} className="px-4 py-2 text-sm text-gray-400 border border-white/10 hover:text-white">Cancel</button>
              <button onClick={executeOverride} disabled={overrideLoading || overrideReason.length < 10}
                className={`px-4 py-2 text-sm font-medium ${overrideReason.length >= 10 ? 'bg-amber-500 text-black hover:bg-amber-500/90' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`}
                data-testid="confirm-override">
                {overrideLoading ? 'Processing...' : 'Apply Override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white mb-6 flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> Back to Property
      </button>

      {/* Unit Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <h1 className="text-2xl font-light text-white">{unit?.unitName}</h1>
            <div className={`px-3 py-1 text-sm font-medium ${STATE_CONFIG[currentState]?.text} ${STATE_CONFIG[currentState]?.color}/20 border border-current`}>
              {currentState}
            </div>
          </div>
          <p className="text-sm text-gray-500">{unit?.projectName || unit?.projectId} · {unit?.llp?.name || 'LLP'}</p>
        </div>
        <button onClick={fetchLifecycle} className="flex items-center gap-2 px-4 py-2 border border-white/10 text-gray-400 hover:text-white">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Funding Progress */}
      <div className="border border-white/10 p-6 mb-8">
        <h2 className="text-sm text-gray-500 uppercase tracking-widest mb-4">Funding Progress</h2>
        <div className="h-4 bg-white/5 mb-4 relative overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#4ADE80] to-[#4ADE80]/60 transition-all duration-500" style={{ width: `${Math.min(fundingPercent, 100)}%` }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-mono text-white">{fundingPercent.toFixed(0)}%</span>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-6">
          <div>
            <p className="text-xs text-gray-500 uppercase mb-1">Capital Target</p>
            <p className="font-mono text-lg text-white">{formatINR(unit?.financial?.capitalTarget || financial?.capitalTarget)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase mb-1">In Escrow</p>
            <p className="font-mono text-lg text-[#4ADE80]">{formatINR(escrowSummary?.totalInEscrow || escrow?.totalCollected)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase mb-1">Balance Remaining</p>
            <p className="font-mono text-lg text-white">{formatINR((unit?.financial?.capitalTarget || financial?.capitalTarget || 0) - (escrowSummary?.totalInEscrow || escrow?.totalCollected || 0))}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase mb-1">Investors</p>
            <p className="font-mono text-lg text-white">{allInvestments.length}</p>
          </div>
        </div>
      </div>

      {/* Admin Actions */}
      <div className="border border-white/10 p-6 mb-8">
        <h2 className="text-sm text-gray-500 uppercase tracking-widest mb-4">Unit State Transitions</h2>
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm text-gray-400">Current: <span className={STATE_CONFIG[currentState]?.text}>{currentState}</span></span>
          <ArrowRight className="w-4 h-4 text-gray-600" />
          
          {(validTransitions || availableTransitions || []).map(state => (
            <button key={state} onClick={() => handleTransition(state)} disabled={transitioning}
              className="px-4 py-2 text-sm border border-white/20 text-white hover:bg-white/10 disabled:opacity-50" data-testid={`transition-${state}-btn`}>
              → {state}
            </button>
          ))}
          
          {currentState === 'FUNDED' && (
            <button onClick={() => setShowEscrowRelease(true)} disabled={transitioning}
              className="px-4 py-2 text-sm border border-orange-500/50 text-orange-400 hover:bg-orange-500/10 disabled:opacity-50" data-testid="release-escrow-btn">
              Release Escrow to LLP
            </button>
          )}
          {currentState === 'ESCROW_RELEASED' && (
            <button onClick={() => setShowReraPayment(true)} disabled={transitioning}
              className="px-4 py-2 text-sm border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 disabled:opacity-50" data-testid="confirm-rera-payment-btn">
              Confirm RERA Payment
            </button>
          )}
          {currentState === 'RERA_PAYMENT_DONE' && (
            <button onClick={handleCloseUnit} disabled={transitioning}
              className="px-4 py-2 text-sm border border-blue-500/50 text-blue-400 hover:bg-blue-500/10 disabled:opacity-50" data-testid="close-unit-btn">
              Close Unit
            </button>
          )}
        </div>
        
        {currentState === 'OPEN' && (
          <p className="text-xs text-gray-500 mt-3 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> Unit will auto-transition to FUNDED when 100% capital is in escrow
          </p>
        )}
        {currentState === 'FUNDED' && (
          <p className="text-xs text-gray-500 mt-3 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> Release escrow to transfer funds to LLP. After release, RERA payment must be confirmed.
          </p>
        )}
      </div>

      {/* Action confirmation modal */}
      {actionModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="action-modal">
          <div className="bg-[#1a1a1a] border border-white/10 p-6 w-[480px] max-w-[90vw]">
            <h3 className="text-white text-base mb-1">{actionModal.config.label}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {actionModal.inv.investorName || actionModal.inv.investor?.name} ({actionModal.inv.investmentId}) — currently {actionModal.inv.state.replace(/_/g, ' ')}
            </p>
            {actionModal.config.needsAmount && (
              <div className="mb-3">
                <label className="text-xs text-gray-500 uppercase mb-1 block">Amount (INR)</label>
                <input type="number" value={actionAmount} onChange={e => setActionAmount(e.target.value)}
                  className={`${modalInputCls} font-mono`} data-testid="action-amount" />
              </div>
            )}
            {actionModal.config.needsAmount && (
              <div className="mb-3">
                <label className="text-xs text-gray-500 uppercase mb-1 block">Transaction Ref (optional)</label>
                <input value={actionTxnId} onChange={e => setActionTxnId(e.target.value)}
                  className={modalInputCls} placeholder="NEFT/UPI reference" data-testid="action-txn-id" />
              </div>
            )}
            <div className="mb-4">
              <label className="text-xs text-gray-500 uppercase mb-1 block">Reason (min 10 chars) *</label>
              <textarea value={actionReason} onChange={e => setActionReason(e.target.value)}
                className={`${modalInputCls} h-20 resize-none`} placeholder="Why is this action being taken?"
                data-testid="action-reason" />
              <p className={`text-xs mt-1 ${actionReason.length >= 10 ? 'text-gray-600' : 'text-red-400'}`}>
                {actionReason.length}/10 characters minimum
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setActionModal(null)} className="px-4 py-2 text-sm text-gray-400 border border-white/10 hover:text-white">Cancel</button>
              <button onClick={executeAction} disabled={actionLoading || actionReason.length < 10}
                className={`px-4 py-2 text-sm font-medium ${actionReason.length >= 10 ? 'bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`}
                data-testid="confirm-action">
                {actionLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Investor Pipeline with Filter + Actions + Override */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm text-gray-500 uppercase tracking-widest">Investor Pipeline ({allInvestments.length})</h2>
          {overdueCount > 0 && (
            <span className="text-xs text-red-400 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {overdueCount} overdue
            </span>
          )}
        </div>
        
        {/* State Filter Pills */}
        <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="investor-state-filter">
          <button
            onClick={() => setStateFilter('ALL')}
            className={`text-xs px-3 py-1.5 border transition-colors ${stateFilter === 'ALL' ? 'border-[#4ADE80]/50 text-[#4ADE80] bg-[#4ADE80]/10' : 'border-white/10 text-gray-500 hover:text-white hover:border-white/20'}`}
            data-testid="filter-ALL">
            All ({allInvestments.length})
          </button>
          {ALL_INV_STATES.filter(s => stateCounts[s]).map(state => {
            const colors = INV_STATE_CONFIG[state] || INV_STATE_CONFIG.INTEREST;
            const isActive = stateFilter === state;
            return (
              <button key={state}
                onClick={() => setStateFilter(state)}
                className={`text-xs px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${isActive ? `${colors.bg} ${colors.color} border-current` : 'border-white/10 text-gray-500 hover:text-white hover:border-white/20'}`}
                data-testid={`filter-${state}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                {state.replace(/_/g, ' ')} ({stateCounts[state]})
              </button>
            );
          })}
        </div>

        {/* Investor Table */}
        <div className="border border-white/10">
          <div className="grid grid-cols-12 gap-3 px-6 py-3 border-b border-white/10 bg-white/5 text-xs text-gray-600 uppercase tracking-wider">
            <div className="col-span-2">Investor</div>
            <div className="col-span-1">State</div>
            <div className="col-span-1 text-right">Amount</div>
            <div className="col-span-2">Broker</div>
            <div className="col-span-1 text-right">Last Action</div>
            <div className="col-span-5 text-right">Actions</div>
          </div>
          
          {filteredInvestments.length > 0 ? (
            filteredInvestments.map(inv => {
              const name = inv.investorName || inv.investor?.name || 'Investor';
              const email = inv.investorEmail || inv.investor?.email || '';
              const broker = inv.brokerName || '-';
              const days = inv.daysSinceLastAction;
              const overdue = inv.isOverdue;
              const actions = inv.actions || [];
              return (
                <div key={inv.investmentId}
                  className={`grid grid-cols-12 gap-3 px-6 py-4 border-b border-white/5 hover:bg-white/[0.03] items-center ${overdue ? 'bg-red-500/[0.03]' : ''}`}
                  data-testid={`investor-row-${inv.investmentId}`}>
                  <div className="col-span-2 min-w-0">
                    <p className="text-sm text-white truncate">{name}</p>
                    <p className="text-xs text-gray-600 truncate">{email}</p>
                  </div>
                  <div className="col-span-1">
                    <StateBadge state={inv.state} />
                  </div>
                  <div className="col-span-1 text-right">
                    <span className="text-sm font-mono text-white">{formatINR(inv.amount || inv.amounts?.totalCommitment)}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-gray-400">{broker}</span>
                  </div>
                  <div className="col-span-1 text-right">
                    {days !== undefined ? (
                      <span className={`text-xs ${overdue ? 'text-red-400 font-medium' : 'text-gray-600'}`}>
                        {days}d ago
                        {overdue && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600">-</span>
                    )}
                  </div>
                  <div className="col-span-5 flex justify-end gap-1.5 flex-wrap">
                    {actions.map(action => {
                      const config = ACTION_MAP[action];
                      if (!config) return null;
                      return (
                        <button key={action} onClick={() => handleAction(inv, action)}
                          className="text-xs px-2 py-1 border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition-colors whitespace-nowrap"
                          data-testid={`action-${action}-${inv.investmentId}`}>
                          {config.label}
                        </button>
                      );
                    })}
                    <OverrideDropdown inv={inv} onOverride={handleOverride} />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-6 py-8 text-center text-sm text-gray-600">
              {stateFilter === 'ALL' ? 'No investments yet' : `No investors in ${stateFilter.replace(/_/g, ' ')} state`}
            </div>
          )}
        </div>
      </div>

      {/* Event Timeline */}
      <div>
        <h2 className="text-sm text-gray-500 uppercase tracking-widest mb-4">Event Timeline</h2>
        <div className="border border-white/10 p-6">
          {(lifecycle?.stateHistory || recentEvents || []).length > 0 ? (
            <div className="space-y-4">
              {(lifecycle?.stateHistory || recentEvents || []).slice().reverse().map((event, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className={`w-2 h-2 mt-2 rounded-full ${
                    event.eventType === 'PAYMENT_RECEIVED' ? 'bg-[#4ADE80]' :
                    event.eventType === 'DOCUMENT_SIGNED' ? 'bg-yellow-500' :
                    event.eventType === 'ESCROW_RELEASED' ? 'bg-purple-500' :
                    'bg-blue-500'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-white">{event.state || event.eventType?.replace(/_/g, ' ')}</span>
                      {event.data?.toState && (
                        <span className="text-xs text-gray-400">{event.data.fromState && `${event.data.fromState} → `}{event.data.toState}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{formatDate(event.timestamp)}</p>
                    {(event.reason || event.data?.reason) && (
                      <p className="text-xs text-gray-400 mt-1">{event.reason || event.data?.reason}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No events recorded</p>
          )}
        </div>
      </div>

      {/* Escrow Release Modal */}
      {showEscrowRelease && (
        <EscrowReleaseModal
          unit={unit} escrowSummary={escrowSummary} investments={investments}
          onConfirm={handleReleaseEscrow} onCancel={() => setShowEscrowRelease(false)}
          loading={transitioning} formatINR={(v) => formatINR(v)}
        />
      )}

      {/* RERA Payment Modal */}
      {showReraPayment && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="rera-payment-modal">
          <div className="bg-black border border-white/10 w-full max-w-lg">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl text-white mb-1">Confirm RERA Payment</h2>
              <p className="text-sm text-gray-500">{unit?.unitName} · {unit?.projectId}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-cyan-500/10 border border-cyan-500/30 p-4">
                <p className="text-sm text-cyan-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Confirms LLP has transferred funds to developer's RERA account.
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">Payment Reference (UTR/NEFT Ref) *</label>
                <input type="text" value={reraPaymentForm.reference}
                  onChange={(e) => setReraPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 text-white focus:border-cyan-500 outline-none"
                  placeholder="Enter bank transaction reference" data-testid="rera-payment-reference" />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">Amount Transferred (INR) *</label>
                <input type="number" value={reraPaymentForm.amount}
                  onChange={(e) => setReraPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 text-white focus:border-cyan-500 outline-none"
                  placeholder="Enter amount" data-testid="rera-payment-amount" />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">Notes (Optional)</label>
                <textarea value={reraPaymentForm.notes}
                  onChange={(e) => setReraPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 text-white focus:border-cyan-500 outline-none h-20 resize-none"
                  placeholder="Any additional notes..." data-testid="rera-payment-notes" />
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-4">
              <button onClick={() => { setShowReraPayment(false); setReraPaymentForm({ reference: '', amount: '', notes: '' }); }}
                className="flex-1 px-4 py-3 border border-white/20 text-gray-400 hover:text-white">Cancel</button>
              <button onClick={handleReraPayment} disabled={transitioning || !reraPaymentForm.reference || !reraPaymentForm.amount}
                className="flex-1 px-4 py-3 bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/30 disabled:opacity-50" data-testid="confirm-rera-btn">
                {transitioning ? <RefreshCw className="w-4 h-4 animate-spin mx-auto" /> : 'Confirm RERA Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EscrowReleaseModal({ unit, escrowSummary, investments, onConfirm, onCancel, loading, formatINR }) {
  const [confirmations, setConfirmations] = useState({ docs: false, bank: false, authorize: false });
  const allConfirmed = confirmations.docs && confirmations.bank && confirmations.authorize;
  const readyInvestments = investments?.filter(inv => inv.state === 'CAPITAL_PAID') || [];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-black border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-[#4ADE80]/30 bg-[#4ADE80]/5">
          <h2 className="text-xl text-white">Unit Fully Funded — Ready for Escrow Release</h2>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-white text-lg">{unit?.unitName}</p>
              <p className="text-sm text-gray-500">{unit?.llp?.name}</p>
            </div>
            <div className="px-3 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/30">FUNDED</div>
          </div>
          <div className="border border-white/10 p-6">
            <h3 className="text-sm text-gray-500 uppercase mb-4">Escrow Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-xs text-gray-500 mb-1">Total in Escrow</p><p className="font-mono text-2xl text-[#4ADE80]">{formatINR(escrowSummary?.totalInEscrow)}</p></div>
              <div><p className="text-xs text-gray-500 mb-1">Ready Investors</p><p className="font-mono text-2xl text-white">{readyInvestments.length}</p></div>
            </div>
          </div>
          <div className="border border-white/10 p-6">
            <h3 className="text-sm text-gray-500 uppercase mb-4">Release Destination</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">LLP:</span><span className="text-white">{unit?.llp?.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Bank:</span><span className="text-white">{unit?.llp?.bankAccount?.bankName || 'Not configured'}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Account:</span><span className="text-white font-mono">{unit?.llp?.bankAccount?.accountNumber || '-'}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">IFSC:</span><span className="text-white font-mono">{unit?.llp?.bankAccount?.ifsc || '-'}</span></div>
            </div>
          </div>
          <div className="border border-yellow-500/30 bg-yellow-500/5 p-6">
            <p className="text-yellow-400 text-sm mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> This action is irreversible. Funds will be transferred to LLP account.
            </p>
            <div className="space-y-3">
              {[['docs', 'I confirm all investor documents are verified'], ['bank', 'I confirm LLP bank details are correct'], ['authorize', `I authorize the release of ${formatINR(escrowSummary?.totalInEscrow)} to ${unit?.llp?.name}`]].map(([key, text]) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={confirmations[key]} onChange={(e) => setConfirmations(prev => ({ ...prev, [key]: e.target.checked }))} className="w-4 h-4" />
                  <span className="text-sm text-white">{text}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <button onClick={onCancel} className="px-6 py-3 border border-white/10 text-gray-400 hover:text-white">Cancel</button>
            <button onClick={onConfirm} disabled={!allConfirmed || loading}
              className="px-6 py-3 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold disabled:opacity-50 flex items-center gap-2">
              {loading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Releasing...</> : <><Unlock className="w-4 h-4" /> Release Escrow</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
